import { useGetStorageLocationsQuery } from "@fero/hooks";
import { useTradeSalesStore } from "@fero/stores";
import { Autocomplete, Grid, TextField } from "@mui/material";
import type { StorageLocationView } from "adl-gen/ferovinum/app/api";
import { useConfirmationDialog } from "components/context/global-dialog/use-dialog";
import { BaseWizardCard } from "components/library/widgets/wizard-card/base-wizard-card";
import type { FormikProps } from "formik";
import type React from "react";
import type { SelectedProductsFormValues } from "../new-organisation-purchase-request-setup-page";

interface LocationSelectorCardProps {
  form: FormikProps<SelectedProductsFormValues>;
}
export const NewLocationSelectorCard = (props: LocationSelectorCardProps) => {
  return (
    <BaseWizardCard
      title={"Storage Location"}
      subtitle={
        "Please select the storage location from where your products are to be sold, or where your finished products will be produced."
      }
      inputElement={
        <Grid container spacing={0}>
          <Grid item xs={12} lg={5} pl={0}>
            <LocationInput {...props} />
          </Grid>
        </Grid>
      }
    />
  );
};

const LocationInput: React.FC<LocationSelectorCardProps> = ({ form }) => {
  const { options, updateOptions } = useTradeSalesStore();
  const { data: storageLocations = [] } = useGetStorageLocationsQuery();
  const { showConfirmationDialog } = useConfirmationDialog();

  /**
   * Make sure user wants to change location since this will reset any product selections and price/quantity details.
   */
  const onChangeLocation = async (
    storageLocationView?: StorageLocationView,
  ) => {
    if ((form.values.selectedProducts?.length || 0) === 0) {
      form.setFieldValue("selectedProducts", []);
      updateOptions({ storageLocation: storageLocationView });
      return;
    }
    const userResponse = await showConfirmationDialog({
      title: "Are you sure you want to change the storage location?",
      body: "You will lose your current product selections and price/quantity details.",
    });
    if (userResponse.kind === "ok") {
      form.setFieldValue("selectedProducts", []);
      updateOptions({ storageLocation: storageLocationView });
    }
  };

  return (
    <Autocomplete<StorageLocationView>
      fullWidth
      value={options.storageLocation ?? null}
      disabled={options.isEditMode}
      onChange={(_e, value) => onChangeLocation(value ?? undefined)}
      getOptionLabel={(loc: StorageLocationView) =>
        loc.storageLocationName || ""
      }
      options={storageLocations}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select storage location"
          name="location"
          required
        />
      )}
      isOptionEqualToValue={(option, value) => option.id === value.id}
    />
  );
};
